import { useMutation } from "@apollo/client/react";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import { useNotificationState, useSchedulerState } from "hooks/state";
import useTimeZone from "hooks/useTimeZone";
import flatten from "lodash/flatten";
import values from "lodash/values";
import GqlError from "utils/GqlError";
import TimeZone from "utils/TimeZone";
import Uuid from "utils/Uuid";
import { insertWorkBlock } from "./helpers/handleUpdateWorkBlocks";
import { ScheduleWorkBlock } from "./workBlock.gql";

export default function useScheduleWorkBlock(refetchShiftInstances) {
  const applicationContext = useApplicationContext();
  const { setNotification } = useNotificationState();
  const [scheduleWorkBlock] = useMutation(ScheduleWorkBlock, {
    onCompleted: () => {
      refetchShiftInstances();
    },
    onError: (error) => {
      GqlError.handler(error, (errors) => {
        const allErrors = flatten(values(errors)).join("\n");

        setNotification({
          message: allErrors,
          title: "Unable to schedule work block",
          type: "danger",
        });
      });
    },
  });
  const {
    state: { dataTimeRange },
  } = useSchedulerState();
  const { overrideToSiteTimeZone } = useTimeZone();

  return (workBlock) => {
    const workBlockId = Uuid.generate();

    const workBlockForOptimisticResponse = {
      effectiveLabor: 10,
      efficiencyOverride: null,
      id: workBlockId,
      laborOverride: null,
      lineEfficiency: 1,
      performance: workBlock.workOrder.performance,
      performanceOverride: null,
      productionRateDependsOnNumberOfPeople: true,
      productionRateOverride: null,
      quantity: 640,
      standardPeople: 10,
      standardProductionRate: workBlock.workOrder.standardProductionRate,
      unitOfMeasure: null,
      ...workBlock,
    };

    return scheduleWorkBlock({
      optimisticResponse: {
        scheduleWorkBlock: {
          __typename: "ScheduleWorkBlockPayload",
          workBlock: workBlockForOptimisticResponse,
        },
      },
      update: (proxy, { data }) => {
        const newWorkBlock = data.scheduleWorkBlock.workBlock;
        const getAllBlocksVariables = {
          endsAt: overrideToSiteTimeZone(dataTimeRange.endDate),
          startsAt: overrideToSiteTimeZone(dataTimeRange.startDate),
        };

        insertWorkBlock(newWorkBlock, proxy, getAllBlocksVariables);
      },
      variables: {
        efficiencyOverride: workBlock.efficiencyOverride,
        id: workBlockId,
        laborOverride: workBlock.laborOverride,
        lineId: workBlock.line.id,
        lockedOnSchedule: workBlock.lockedOnSchedule,
        performanceOverride: workBlock.performanceOverride,
        productionRateOverride: workBlock.productionRateOverride,
        teardownTimeDuration: workBlock.teardownTimeDuration || 0,
        teardownTimeLabel: workBlock.teardownTimeLabel || "Teardown Time",
        workOrderId: workBlock.workOrder.id,
        workTimeEndsAt: TimeZone.overrideZone(workBlock.workTimeEndsAt, applicationContext.timeZone.name),
        workTimeStartsAt: TimeZone.overrideZone(workBlock.workTimeStartsAt, applicationContext.timeZone.name),
      },
    });
  };
}
